/**
 * Isomorphic validation utilities
 * Pure functions that work identically in Node.js and Browser
 * IMPORTANT: No runtime-specific dependencies
 */

import {
  WALLET_ADDRESS_MAX_LENGTH_FOR_REGEX,
  compileRegExpPatternOrNull,
  isEffectivelyAnchoredPattern,
} from './regex-anchoring.js'
import { LEDGER_AMOUNT_NUMERIC_SCALE } from '../constants/ledger-amount-scale.js'

/**
 * Common validation patterns
 * These regex patterns are safe to share between frontend and backend
 */
export const VALIDATION_PATTERNS = {
  // Basic patterns
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[1-9]\d{1,14}$/,
  UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,

  // Password patterns (for client-side STRENGTH feedback only — never enforcement).
  // The enforced policy is passwordValidation in platform/validation/common.ts
  // (8-128 chars, ≥1 lowercase, ≥1 digit — no character whitelist).
  // "Strong" = variety feedback: lower + upper + digit + any non-alphanumeric.
  PASSWORD_STRONG: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/,
  PASSWORD_MEDIUM: /^(?=.*[a-zA-Z])(?=.*\d)/,

  // Crypto patterns (for display validation)
  BITCOIN_ADDRESS: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
  ETHEREUM_ADDRESS: /^0x[a-fA-F0-9]{40}$/,

  // Financial patterns
  //
  // El tope de decimales es la ESCALA DE LA COLUMNA, construida desde
  // `LEDGER_AMOUNT_NUMERIC_SCALE`, no escrita a mano. Con el `8` literal que
  // había aquí, este patrón rechazaba un importe de 9 decimales que
  // `bat.transactions` guarda sin inmutarse desde la migración 0357 — la misma
  // discrepancia que hacía que la API contestara 400 a lo que el importador SÍ
  // escribía (issue #2198).
  AMOUNT_DECIMAL: new RegExp(`^\\d+(\\.\\d{1,${LEDGER_AMOUNT_NUMERIC_SCALE}})?$`),
  CURRENCY_CODE: /^[A-Z]{3}$/,

  // URL patterns
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,

  // Date patterns
  DATE_ISO: /^\d{4}-\d{2}-\d{2}$/,
  DATETIME_ISO: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/,
} as const

/**
 * Validate email addresses
 */
export function validateEmail(email: string): boolean {
  return VALIDATION_PATTERNS.EMAIL.test(email.trim().toLowerCase())
}

/**
 * Validate a wallet address against a per-chain regex source string
 * (`bat.blockchain_networks.address_pattern`, served by /config/validation-rules).
 *
 * Chain-agnostic on purpose (#2043): the caller resolves the pattern by network
 * slug; this function only answers "does the string match". Never throws.
 *
 * An UNANCHORED pattern is refused outright (`false`, fix cycle 7 · item 8):
 * `0x[a-fA-F0-9]{40}` would accept any string that merely CONTAINS an address,
 * and this function gates wallet-import source creation for every tenant. The
 * admin zod layer and both seed validators already refuse such a pattern at
 * write time; this is the last line for a row written out-of-band.
 */
export function validateAddressAgainstPattern(
  address: string,
  pattern: string,
  maxLength: number = WALLET_ADDRESS_MAX_LENGTH_FOR_REGEX,
): boolean {
  // ReDoS guard: the pattern is operator-editable data, so bound the input
  // length BEFORE handing it to the regex engine (see the shared constant).
  if (address.length > maxLength) return false

  // `^…$` with no top-level `|` — same structural check as the zod schemas.
  if (!isEffectivelyAnchoredPattern(pattern)) return false

  // Malformed / empty / over-long pattern in the catalog → "no match", never a crash.
  const regex = compileRegExpPatternOrNull(pattern)
  if (regex === null) return false
  return regex.test(address)
}

/**
 * Validate date strings
 */
export function validateDateString(
  dateString: string,
  format: 'ISO' | 'DATETIME_ISO' = 'ISO',
): boolean {
  const pattern = format === 'ISO' ? VALIDATION_PATTERNS.DATE_ISO : VALIDATION_PATTERNS.DATETIME_ISO

  if (!pattern.test(dateString)) return false

  // Also validate that it's a real date
  const date = new Date(dateString)
  return !isNaN(date.getTime())
}

/**
 * Validate file types and sizes (for upload validation)
 */
export function validateFile(
  fileName: string,
  fileSize: number,
  allowedTypes: string[],
  maxSize: number,
): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  // Check file extension
  const extension = fileName.toLowerCase().split('.').pop()
  if (!extension || !allowedTypes.includes(extension)) {
    errors.push(`File type .${extension} is not allowed. Allowed types: ${allowedTypes.join(', ')}`)
  }

  // Check file size
  if (fileSize > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024))
    errors.push(`File size exceeds ${maxSizeMB}MB limit`)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}
